import { useEffect, useRef, useState } from "react";
import { CircleDot, LogIn, Menu, ShieldCheck, WalletCards, X } from "lucide-react";
import { apiJson } from "./api";
import { AuthModal } from "./components/AuthModal";
import { BillingModal } from "./components/BillingModal";
import { ChatView } from "./components/ChatView";
import { ProfilePanel, profileCompletion } from "./components/ProfilePanel";
import type {
  AccountUser,
  BillingProduct,
  CandidateProfile,
  CreditWallet,
  Message,
  ServiceStatus
} from "./types";

const PROFILE_KEY = "zhiyuan-zhishuo:profile:v1";
const MESSAGES_KEY = "zhiyuan-zhishuo:messages:v1";

const defaultProfile: CandidateProfile = {
  name: "",
  province: "",
  examYear: String(new Date().getFullYear()),
  subjectType: "",
  score: "",
  rank: "",
  familyBudget: "",
  targetCities: "",
  interests: "",
  familyResources: "",
  constraints: ""
};

function parseQueryParam(key: string): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(key);
}

function cleanAlipayQuery() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.has("alipay_order")) {
    url.searchParams.delete("alipay_order");
    window.history.replaceState({}, "", url.toString());
  }
}

export default function App() {
  const [profile, setProfile] = useState<CandidateProfile>(() =>
    loadLocal(PROFILE_KEY, defaultProfile)
  );
  const [messages, setMessages] = useState<Message[]>(() => loadLocal(MESSAGES_KEY, []));
  const [profileExpanded, setProfileExpanded] = useState(true);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus | null>(null);
  const [user, setUser] = useState<AccountUser | null>(null);
  const [wallet, setWallet] = useState<CreditWallet | null>(null);
  const [products, setProducts] = useState<BillingProduct[]>([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [alipayStatus, setAlipayStatus] = useState<{ orderId: string; status: "checking" | "paid" | "pending" | "error"; message: string } | null>(null);
  const pollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  }, [profile]);

  useEffect(() => {
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages.filter((message) => message.status !== "streaming")));
  }, [messages]);

  useEffect(() => {
    apiJson<ServiceStatus>("/api/health")
      .then(setServiceStatus)
      .catch(() =>
        setServiceStatus({
          ok: false,
          configured: false,
          provider: "后端服务",
          model: null
        })
      );
    apiJson<{ products: BillingProduct[] }>("/api/billing/products")
      .then((result) => setProducts(result.products))
      .catch(() => setProducts([]));
    apiJson<{ user: AccountUser | null }>("/api/auth/me")
      .then((result) => {
        setUser(result.user);
        if (result.user) void refreshWallet();
      })
      .catch(() => setUser(null));

    const alipayOrder = parseQueryParam("alipay_order");
    if (alipayOrder) {
      cleanAlipayQuery();
      setAlipayStatus({ orderId: alipayOrder, status: "checking", message: "正在确认支付状态…" });
      let attempts = 0;
      const poll = async () => {
        if (attempts >= 15) {
          setAlipayStatus((prev) => prev ? { ...prev, status: "pending", message: "支付确认中，如已扣款请稍后刷新页面查看额度。" } : null);
          return;
        }
        attempts++;
        try {
          const data = await apiJson<{ status: string; tradeNo?: string }>(`/api/billing/orders/${encodeURIComponent(alipayOrder)}/status`);
          if (data.status === "paid") {
            setAlipayStatus({ orderId: alipayOrder, status: "paid", message: "支付已完成，额度已到账。" });
            void refreshWallet();
            return;
          }
          if (data.status === "pending") {
            setAlipayStatus({ orderId: alipayOrder, status: "checking", message: "支付确认中，请稍候…" });
            pollTimerRef.current = window.setTimeout(poll, 2000);
          } else {
            setAlipayStatus({ orderId: alipayOrder, status: "error", message: "订单未成功支付，如有疑问请联系客服。" });
          }
        } catch {
          setAlipayStatus({ orderId: alipayOrder, status: "error", message: "网络异常，请稍后刷新页面查看额度。" });
        }
      };
      pollTimerRef.current = window.setTimeout(poll, 1500);
    }

    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current);
      }
    };
  }, []);

  const completion = profileCompletion(profile);
  const refreshWallet = async () => {
    try {
      setWallet(await apiJson<CreditWallet>("/api/billing/wallet"));
    } catch {
      setWallet(null);
    }
  };

  const authenticated = (account: AccountUser) => {
    setUser(account);
    void refreshWallet();
  };

  const logout = async () => {
    await apiJson("/api/auth/logout", { method: "POST" }).catch(() => null);
    setUser(null);
    setWallet(null);
    setMessages([]);
    setBillingOpen(false);
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="志愿直说首页">
          <span className="brand-mark" aria-hidden="true">直</span>
          <span>
            <strong>志愿直说</strong>
            <small>高考志愿决策助手</small>
          </span>
        </a>

        <div className="topbar-status">
          {/* <span
            className={
              serviceStatus?.configured ? "service-pill service-pill--ready" : "service-pill"
            }
          >
            <CircleDot size={14} />
            {serviceStatus === null
              ? "检查模型"
              : serviceStatus.configured
                ? `${serviceStatus.provider}已连接`
                : "模型待配置"}
          </span> */}
          {user ? (
            <button
              className="account-button"
              type="button"
              onClick={() => setBillingOpen(true)}
              aria-label={`账户 ${user.email}，可用 ${wallet?.balance ?? 0} 次`}
            >
              <WalletCards size={16} />
              <span>{wallet?.balance ?? 0} 次</span>
              <small>{user.email}</small>
            </button>
          ) : (
            <button className="login-button" type="button" onClick={() => setAuthOpen(true)}>
              <LogIn size={16} />
              登录 / 注册
            </button>
          )}
          <a className="privacy-note legal-topbar-link" href="/privacy.html">
            <ShieldCheck size={15} />
            隐私政策
          </a>
          <button
            className="mobile-menu-button"
            type="button"
            onClick={() => setMobilePanelOpen(true)}
            aria-label="打开考生档案"
          >
            <Menu size={21} />
            <span>{completion}%</span>
          </button>
        </div>
      </header>


      <section
        className="home-banner"
        aria-label="购买入口"
        role="button"
        tabIndex={0}
        onClick={() => (user ? setBillingOpen(true) : setAuthOpen(true))}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            user ? setBillingOpen(true) : setAuthOpen(true);
          }
        }}
      >
        <div className="home-copy">
          <span className="eyebrow">点击购买额度</span>
          <strong>支付宝购买，即时到账</strong>
          <p>登录后选择额度包，使用支付宝完成支付，额度即时到账。</p>
        </div>
      </section>

      <main className="workspace">
        <aside className="sidebar">
          <ProfilePanel
            profile={profile}
            onChange={setProfile}
            expanded={profileExpanded}
            onToggle={() => setProfileExpanded((value) => !value)}
          />
          <div className="sidebar-footnote">
            <strong>这不是算命。</strong>
            <p>AI 负责帮你建立取舍顺序；最终志愿表必须用当年官方数据复核。</p>
          </div>
        </aside>

        <ChatView
          messages={messages}
          setMessages={setMessages}
          profile={profile}
          serviceStatus={serviceStatus}
          user={user}
          credits={wallet?.balance ?? null}
          onRequireAuth={() => setAuthOpen(true)}
          onRequireCredits={() => setBillingOpen(true)}
          onCreditChanged={refreshWallet}
        />
      </main>

      {mobilePanelOpen && (
        <div className="mobile-drawer-backdrop" role="presentation" onMouseDown={() => setMobilePanelOpen(false)}>
          <aside
            className="mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="考生档案"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="mobile-drawer__top">
              <strong>考生档案</strong>
              <button type="button" onClick={() => setMobilePanelOpen(false)} aria-label="关闭">
                <X size={21} />
              </button>
            </div>
            <ProfilePanel
              profile={profile}
              onChange={setProfile}
              expanded
              onToggle={() => undefined}
            />
          </aside>
        </div>
      )}

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onAuthenticated={authenticated}
      />
      <BillingModal
        open={billingOpen}
        user={user}
        wallet={wallet}
        products={products}
        serviceStatus={serviceStatus}
        onClose={() => setBillingOpen(false)}
        onWalletChange={setWallet}
        onLogout={() => void logout()}
      />

      {alipayStatus && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setAlipayStatus(null)}>
          <div
            className="billing-modal"
            role="alertdialog"
            aria-live="polite"
            onMouseDown={(event) => event.stopPropagation()}
            style={{ maxWidth: 420 }}
          >
            <header className="modal-header">
              <div>
                <span className="eyebrow">支付结果</span>
                <h2>
                  {alipayStatus.status === "paid"
                    ? "支付已完成"
                    : alipayStatus.status === "checking"
                      ? "正在确认支付"
                      : "支付状态"}
                </h2>
              </div>
              <button className="modal-close" type="button" onClick={() => setAlipayStatus(null)} aria-label="关闭">
                <X size={20} />
              </button>
            </header>
            <div style={{ padding: "16px 0", textAlign: "center" }}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                  background: alipayStatus.status === "paid" ? "#d4edda" : alipayStatus.status === "checking" ? "#e2e3e5" : "#f8d7da",
                  color: alipayStatus.status === "paid" ? "#155724" : alipayStatus.status === "checking" ? "#383d41" : "#721c24",
                }}
              >
                {alipayStatus.status === "paid" ? "✓" : alipayStatus.status === "checking" ? "⟳" : "!"}
              </div>
              <p style={{ margin: "8px 0", fontSize: 15, color: "#333" }}>{alipayStatus.message}</p>
              {alipayStatus.status === "paid" && (
                <p style={{ margin: "4px 0", fontSize: 13, color: "#666" }}>
                  当前可用额度：{wallet?.balance ?? 0} 次
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", paddingBottom: 8 }}>
              <button
                type="button"
                className="login-button"
                onClick={() => setAlipayStatus(null)}
              >
                知道了
              </button>
              {alipayStatus.status === "pending" && (
                <button
                  type="button"
                  className="account-button"
                  onClick={() => window.location.reload()}
                >
                  刷新页面
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function loadLocal<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
}
